// ProductionExecutionReport.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/en";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// rate on a production run is what one unit cost to make, worked out from the item's recipe
// when the run was saved. Runs with no recipe have no rate, so no cost or margin.
const unitCost = (row) => {
  const rate = parseFloat(row.rate);
  return rate > 0 ? rate : null;
};
const productionCost = (row) => {
  const cost = unitCost(row);
  return cost != null ? cost * (parseFloat(row.qty) || 0) : null;
};
const margin = (row) => {
  const cost = productionCost(row);
  return cost != null ? (parseFloat(row.amount) || 0) - cost : null;
};

const ProductionExecutionReport = () => {
  const [branchCode, setBranchCode] = useState("ALL");
  const [branches, setBranches] = useState([]);
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [executionData, setExecutionData] = useState([]);
  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState("ProductionExecutionReport.xlsx");

  const fetchBranches = async () => {
    try {
      const tenancyId = localStorage.getItem("tenancyId");
      const token = localStorage.getItem("jwtToken");
      const res = await fetch(`/api/${tenancyId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch branches");
      const data = await res.json();
      const allBranches = Array.isArray(data) ? data : data.branches || data.data || [];
      const uniqueBranches = [...new Map(allBranches.map((b) => [b.branchCode, b])).values()];
      setBranches(uniqueBranches);
    } catch (e) {
      console.error("Error fetching branches:", e);
      setBranches([]);
    }
  };

  const fetchExecutionData = async () => {
    if (!fromDate || !toDate) return;
    if (!branchCode || branchCode === "ALL") {
      alert("Please select a branch.");
      return;
    }
    try {
      const tenancyId = localStorage.getItem("tenancyId");
      const token = localStorage.getItem("jwtToken");
      const params = new URLSearchParams({ branchCode, fromDate, toDate });
      const response = await fetch(
        `/api/${tenancyId}/production-execution?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setExecutionData(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Error fetching production execution data:", error);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(executionData.map(row => ({
      ...row,
      cost_per_unit: unitCost(row),
      production_cost: productionCost(row),
      margin: margin(row),
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Production Execution");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), fileName);
    setOpen(false);
  };

  const totalQty = Array.isArray(executionData)
    ? executionData.reduce((total, item) => total + (parseFloat(item.qty) || 0), 0)
    : 0;

  const totalAmount = Array.isArray(executionData)
    ? executionData.reduce((total, item) => total + (parseFloat(item.amount) || 0), 0)
    : 0;

  const totalCost = Array.isArray(executionData)
    ? executionData.reduce((total, item) => total + (productionCost(item) || 0), 0)
    : 0;

  const totalMargin = Array.isArray(executionData)
    ? executionData.reduce((total, item) => total + (margin(item) || 0), 0)
    : 0;

  return (
    <Box sx={{ flexGrow: 1, p: 3, ml: "240px", mt: 2 }}>
      {/* Branch */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="branch-label">Branch</InputLabel>
          <Select
            labelId="branch-label"
            value={branchCode}
            label="Branch"
            onChange={(e) => setBranchCode(e.target.value)}
          >
            <MenuItem value="ALL">ALL</MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.branchCode} value={b.branchCode}>
                {b.branchCode} {b.branchName ? `- ${b.branchName}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Dates */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <TextField
          type="date"
          label="From Date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1, mr: 2 }}
        />
        <TextField
          type="date"
          label="To Date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Buttons */}
      <Box sx={{ mb: 3 }}>
        <Button variant="contained" color="primary" onClick={fetchExecutionData} sx={{ mr: 2 }}>
          Fetch Production Execution
        </Button>
        <Button variant="contained" color="secondary" onClick={handleClickOpen}>
          Export to Excel
        </Button>
      </Box>

      {/* Export dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Export to Excel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the file name for the Excel file.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="File Name"
            type="text"
            fullWidth
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">Cancel</Button>
          <Button onClick={handleExport} color="primary">Export</Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <TableContainer component={Paper} sx={{ width: "100%", mt: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Voucher No</TableCell>
              <TableCell sx={{ color: "#fff" }}>Planning Voucher No</TableCell>
              <TableCell sx={{ color: "#fff" }}>Voucher Date</TableCell>
              <TableCell sx={{ color: "#fff" }}>Branch</TableCell>
              <TableCell sx={{ color: "#fff" }}>Item Name</TableCell>
              <TableCell sx={{ color: "#fff" }}>Batch</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Qty</TableCell>
              <TableCell sx={{ color: "#fff" }}>Unit</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Standard Price</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Amount</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Cost per Unit</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Production Cost</TableCell>
              <TableCell sx={{ color: "#fff" }} align="right">Margin</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executionData.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell>{row.voucher_number}</TableCell>
                <TableCell>{row.planningVoucherNumber}</TableCell>
                <TableCell>{row.voucher_date}</TableCell>
                <TableCell>{row.branch_code}</TableCell>
                <TableCell>{row.item_name}</TableCell>
                <TableCell>{row.batch}</TableCell>
                <TableCell align="right">{row.qty}</TableCell>
                <TableCell>{row.unit}</TableCell>
                <TableCell align="right">{row.standard_price}</TableCell>
                <TableCell align="right">{row.amount}</TableCell>
                <TableCell align="right">{unitCost(row) != null ? unitCost(row).toFixed(2) : "-"}</TableCell>
                <TableCell align="right">{productionCost(row) != null ? productionCost(row).toFixed(2) : "-"}</TableCell>
                <TableCell align="right">{margin(row) != null ? margin(row).toFixed(2) : "-"}</TableCell>
              </TableRow>
            ))}

            {/* Totals row */}
            <TableRow>
              <TableCell colSpan={6} sx={{ fontWeight: "bold" }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalQty.toFixed(2)}</TableCell>
              <TableCell />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalAmount.toFixed(2)}</TableCell>
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalCost.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalMargin.toFixed(2)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductionExecutionReport;
